/**
 * VSME API Client for Companies Admin App
 *
 * Provides methods to interact with the VSME REST API endpoints
 */

import { __ } from '@/hub-shared/utils/i18n';
import { extractHubErrorFromPayload, HubApiError } from '@tpct/mission-core/utils/hubApiError';
import { resolveCanonicalId } from '@vsme-shared-utils/disclosureIdResolver';
import { missionStartSubscriptionRequiredMessage } from '@tpct/mission-core/utils/trialDossierMissionGate';

const HTTP_STATUS_MESSAGES: Record<number, string> = {
  400: __('Requête invalide.'),
  401: __('Authentification requise ou session expirée.'),
  403: __('Accès refusé.'),
  404: __('Ressource introuvable.'),
  408: __('Délai dépassé. Réessayez.'),
  409: __('Conflit.'),
  413: __('Fichier trop volumineux.'),
  415: __('Format non pris en charge.'),
  422: __('Données invalides.'),
  429: __('Trop de requêtes. Patientez puis réessayez.'),
  500: __('Le service est momentanément indisponible. Réessayez plus tard.'),
  502: __('Le service est momentanément indisponible. Réessayez plus tard.'),
  503: __('Le service est momentanément indisponible. Réessayez plus tard.'),
  504: __('Le service est momentanément indisponible. Réessayez plus tard.'),
};

const technicalMessagePatterns: RegExp[] = [
  /^Unable to find REST API nonce$/i,
  /^HTTP error! status:/i,
  /^Request failed$/i,
  /^Failed to fetch$/i,
  /^Failed to load\b/i,
  /^NetworkError when attempting to fetch resource/i,
  /^Load failed$/i,
  /^The user aborted a request\.?$/i,
  /^AbortError$/i,
  /^TypeError:\s*Failed to fetch/i,
  /^TypeError:\s*Load failed/i,
  /^fetch failed$/i,
];

const isStackLike = (value: string): boolean => /\n\s*at\s+/.test(value) || value.length > 800;

const toUserMessage = (value: string, fallback: string): string => {
  const trimmed = value.trim();
  if (trimmed === '' || isStackLike(trimmed)) {
    return fallback;
  }
  if (technicalMessagePatterns.some((pattern) => pattern.test(trimmed))) {
    return fallback;
  }
  return trimmed;
};

const httpStatusMessage = (status: number): string =>
  HTTP_STATUS_MESSAGES[status] ?? (status >= 500
    ? __('Le service est momentanément indisponible. Réessayez plus tard.')
    : __('Une erreur est survenue.'));

const parseErrorPayloadMessage = async (response: Response): Promise<string | null> => {
  const raw = await response.json().catch(() => null) as unknown;
  if (raw === null || typeof raw !== 'object') {
    return null;
  }

  const payload = raw as {
    message?: unknown;
    error?: unknown;
    data?: { message?: unknown };
  };

  if (typeof payload.message === 'string') {
    return payload.message;
  }
  if (typeof payload.error === 'string') {
    return payload.error;
  }
  if (typeof payload.data?.message === 'string') {
    return payload.data.message;
  }
  return null;
};

/**
 * VSME Report interface (summary format from listReports)
 */
export interface VSMEReportSummary {
  id: string;
  companyId: number;
  companyName: string;
  missionId?: number | null;
  status: string;
  lifecycle_state: 'current' | 'expired';
  completionPercentage: number;
  createdAt: string;
  modifiedAt: string;
  generatedAt: string | null;
  files: {
    pdf: string | null;
    excel: string | null;
    html: string | null;
    docx: string | null;
  };
  fiscal_year: {
    start: string;
    end: string;
  };
}

/**
 * VSME Report interface (full format from createReport)
 */
export interface VSMEReport {
  id: string;
  company_id: number;
  tenant_id: number;
  fiscal_year: {
    start: string;
    end: string;
  };
  status: string;
  completion_percentage: number;
  is_completed: boolean;
  files: {
    pdf: string | null;
    excel: string | null;
    html: string | null;
    docx: string | null;
    xbrl: string | null;
  };
  timestamps: {
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
  };
}

export interface MissionSummary {
  mission: {
    id: number;
    company_id: number;
    tool_slug: string;
    title: string;
    status: string;
    state: 'ongoing' | 'completed' | 'closed' | 'archived' | 'stale_period';
    period_start: string;
    period_end: string;
    responsable_user_id?: number | null;
    responsable_display_name?: string | null;
    current_step_key: string | null;
    archived_at?: string | null;
    retention_until?: string | null;
    can_continue?: boolean;
  };
  steps: Array<{
    key: string;
    label: string;
    url: string;
    status: string;
    enabled: boolean;
    order: number;
  }>;
  next_step_url: string | null;
  current_step: {
    key: string;
    label: string;
    url: string;
    status: string;
    enabled: boolean;
    order: number;
  } | null;
  linked_report_id: number | null;
}

/** Payload from POST company missions after architecture exposes reuse vs create. */
export type MissionStartPayload = MissionSummary & {
  mission_url?: string;
  existing_mission?: boolean;
};

/**
 * VSME Reports list response
 */
export interface VSMEReportsListResponse {
  reports: VSMEReportSummary[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
    has_more: boolean;
  };
  filters_applied: Record<string, unknown>;
}

/**
 * VSME API response wrapper
 */
interface VSMEApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Get VSME REST API base URL
 */
function getVSMEApiBaseUrl(): string {
  // Use WordPress REST API base URL
  const adminUrl = window.location.origin;
  return `${adminUrl}/wp-json/vsme/v1`;
}

/**
 * Get WordPress REST API nonce from bootstrap data
 */
function getNonce(): string {
  const typedWindow = window as Window & {
    AccountingHubCompaniesAdmin?: { nonce?: string };
    AccountingHubPortal?: { nonce?: string };
    VSMEReportsAdmin?: { nonce?: string };
  };
  const bootstrap = typedWindow.AccountingHubCompaniesAdmin;
  if (bootstrap?.nonce) {
    return bootstrap.nonce;
  }
  const portalBootstrap = typedWindow.AccountingHubPortal;
  if (portalBootstrap?.nonce) {
    return portalBootstrap.nonce;
  }
  const vsmeBootstrap = typedWindow.VSMEReportsAdmin;
  if (vsmeBootstrap?.nonce) {
    return vsmeBootstrap.nonce;
  }
  throw new Error(__('Impossible de trouver le jeton REST API.'));
}

/**
 * Make VSME API request
 */
async function vsmeApiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const baseUrl = getVSMEApiBaseUrl();
  const url = `${baseUrl}/${endpoint.replace(/^\//, '')}`;
  
  const nonce = getNonce();
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': nonce,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const message = await parseErrorPayloadMessage(response);
    throw new Error(toUserMessage(message ?? '', httpStatusMessage(response.status)));
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  if (!text || text.trim() === '') {
    return undefined as T;
  }

  const data: VSMEApiResponse<T> = JSON.parse(text);
  
  // VSME API returns data wrapped in success/data structure
  if (data.success === false) {
    throw new Error(toUserMessage(data.message || '', __('La requête a échoué.')));
  }
  
  // If success is true, data is wrapped in the 'data' property
  // If success is undefined (legacy format), return data directly
  if (data.success === true && data.data !== undefined) {
    return data.data;
  }
  
  return data as T;
}

/**
 * List VSME reports for a company
 */
export async function listVSMEReports(companyId: number): Promise<VSMEReportSummary[]> {
  const response = await vsmeApiRequest<VSMEReportsListResponse>(
    `reports?company_id=${companyId}`
  );
  return response.reports || [];
}

/**
 * Create a new VSME report
 */
export async function createVSMEReport(
  companyId: number,
  fiscalYearStart: string,
  fiscalYearEnd: string,
  missionId: number
): Promise<VSMEReport> {
  const response = await vsmeApiRequest<VSMEReport>('reports', {
    method: 'POST',
    body: JSON.stringify({
      company_id: companyId,
      fiscal_year_start: fiscalYearStart,
      fiscal_year_end: fiscalYearEnd,
      mission_id: missionId,
    }),
  });
  
  return response;
}

export async function generateVSMEReport(reportId: number): Promise<unknown> {
  return vsmeApiRequest<unknown>(`form/generate/${reportId}`, {
    method: 'POST',
  });
}

/**
 * Start a cabinet self mission (always allowed on trial).
 */
export async function startCabinetMission(
  toolSlug = 'vsme',
  responsableUserId?: number | null,
): Promise<MissionStartPayload> {
  const response = await fetch(`${window.location.origin}/wp-json/accounting-hub/v1/cabinet/missions`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': getNonce(),
    },
    body: JSON.stringify({
      tool_slug: toolSlug,
      responsable_user_id: responsableUserId ?? undefined,
    }),
  });

  const raw = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw extractHubErrorFromPayload(
      raw,
      response.status,
      httpStatusMessage(response.status),
      (message, status) => toUserMessage(message, httpStatusMessage(status)),
    );
  }

  if (typeof raw !== 'object' || raw === null || !('success' in raw) || raw.success !== true || !('data' in raw)) {
    throw new Error(__('Réponse mission invalide.'));
  }

  const data = (raw as { data: MissionStartPayload }).data;

  return {
    ...data,
    existing_mission: Boolean(data.existing_mission),
  };
}

/**
 * Start a mission for a company
 */
export async function startMission(
  companyId: number,
  toolSlug = 'vsme',
  responsableUserId?: number | null,
): Promise<MissionStartPayload> {
  const response = await fetch(`${window.location.origin}/wp-json/accounting-hub/v1/company/${companyId}/missions`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': getNonce(),
    },
    body: JSON.stringify({
      tool_slug: toolSlug,
      responsable_user_id: responsableUserId ?? undefined,
    }),
  });

  const raw = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = extractHubErrorFromPayload(
      raw,
      response.status,
      httpStatusMessage(response.status),
      (message, status) => toUserMessage(message, httpStatusMessage(status)),
    );
    if (error.code === 'trial.dossier_mission_denied') {
      throw new HubApiError(missionStartSubscriptionRequiredMessage(), error.code, error.status);
    }
    throw error;
  }

  if (typeof raw !== 'object' || raw === null || !('success' in raw) || raw.success !== true || !('data' in raw)) {
    throw new Error(__('Réponse mission invalide.'));
  }

  const data = (raw as { data: MissionStartPayload }).data;

  return {
    ...data,
    existing_mission: Boolean(data.existing_mission),
  };
}

export async function fetchMissionSummary(missionId: number): Promise<MissionSummary> {
  const response = await fetch(`${window.location.origin}/wp-json/accounting-hub/v1/missions/${missionId}`, {
    credentials: 'same-origin',
    headers: {
      'X-WP-Nonce': getNonce(),
    },
  });

  const raw = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      typeof raw === 'object' && raw !== null && 'message' in raw && typeof (raw as { message?: unknown }).message === 'string'
        ? (raw as { message: string }).message
        : null;
    throw new Error(toUserMessage(message ?? '', httpStatusMessage(response.status)));
  }

  if (typeof raw !== 'object' || raw === null || !('success' in raw) || raw.success !== true || !('data' in raw)) {
    throw new Error(__('Réponse mission invalide.'));
  }

  return (raw as { data: MissionSummary }).data;
}

export async function archiveMission(missionId: number): Promise<MissionSummary> {
  const response = await fetch(`${window.location.origin}/wp-json/accounting-hub/v1/missions/${missionId}/archive`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': getNonce(),
    },
  });

  const raw = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      typeof raw === 'object' && raw !== null && 'message' in raw && typeof (raw as { message?: unknown }).message === 'string'
        ? (raw as { message: string }).message
        : null;
    throw new Error(toUserMessage(message ?? '', httpStatusMessage(response.status)));
  }

  if (typeof raw !== 'object' || raw === null || !('success' in raw) || raw.success !== true || !('data' in raw)) {
    throw new Error(__('Réponse mission invalide.'));
  }

  return (raw as { data: MissionSummary }).data;
}

/**
 * Build VSME form URL
 */
export function buildVSMEFormUrl(
  reportId: string,
  companyId: number,
  missionId?: number | null,
  stepId?: string | null,
): string {
  const baseUrl = window.location.origin;
  const params = new URLSearchParams({
    report_id: reportId,
    company_id: String(companyId),
  });
  if (missionId !== undefined && missionId !== null) {
    params.set('mission_id', String(missionId));
  }
  if (stepId !== undefined && stepId !== null && stepId !== '') {
    const canonicalStepId = resolveCanonicalId(stepId) ?? stepId;
    params.set('step', canonicalStepId);
  }
  return `${baseUrl}/vsme-form/?${params.toString()}`;
}

/**
 * Navigate to VSME form
 */
export function navigateToVSMEForm(reportId: string, companyId: number, missionId?: number | null): void {
  window.location.href = buildVSMEFormUrl(reportId, companyId, missionId);
}
